import { Fragment, useMemo, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "@apollo/client/react";
import { LatestMatchResultLink } from "@/components/match/LatestMatchResultLink";
import { MatchCard } from "@/components/match/MatchCard";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { userErrorMessage } from "@/lib/user-error";
import { localizedMessageOf } from "@/lib/localized-error";
import { ExtendedSearchPage } from "@/pages/ExtendedSearchPage";
import { SaveCollectionSidebar, type GroupSelection } from "@/components/save/SaveCollectionTemplate";
import { ProjectMatchResults } from "../ProjectMatchResults";
import type { LinkedMatch } from "../use-linked-matches";
import { Button } from "@/components/design/Button";
import type { KindMatchFacetTexts } from "../dashboard-kinds";
import { Switch } from "@/components/design/Switch";
import {
  MatchListDocument,
  MatchMode,
  MatchResultListDocument,
  MyPersonGroupsDocument,
  SortOrder,
} from "@/graphql/generated/operations";

// Wen holt man ins Projekt? Drei Wege, und alle drei arbeiten inzwischen mit
// echten Daten.
//
// 1. SUCHE UND COMMUNITY sind EIN Feld, nicht zwei Listen: solange nichts
//    getippt ist, stehen die eigenen Kontakte da (die Community, aus der man
//    normalerweise schöpft), sobald jemand tippt, sucht dieselbe Liste über
//    alle. Ein Umschalter dazwischen wäre eine Frage, die niemand beantworten
//    will - man sucht einfach.
// 2. MATCHES sind die eigenen, mit einem Schalter "nur mit Ergebnis": ein Match
//    ohne Ergebnis kann man nicht übernehmen, es wäre eine leere Zeile mit
//    einem Knopf, der nichts tut.
// 3. ZU MATCH führt weiter, aber erst durch einen Zwischenschritt, der zeigt,
//    was aus dem Projekt mitgeht.
//
// Kandidaten sind Gefragte, keine Mitglieder: sie bekommen keinen Zugriff auf
// das Projekt, sind aber zuweisbar. Wer eine Aufgabe übernehmen soll, muss
// vorher gefragt worden sein, nicht schon zugesagt haben.

/**
 * Welcher der drei Wege gewählt ist. Liegt bei der SEITE, nicht hier: der
 * Team-Reiter springt herüber und will dabei sagen, welcher Weg sich öffnen
 * soll - "Weitere suchen" meint einen anderen als "Ich will matchen".
 */
export type CandidatePath = "known" | "existingMatch" | "match" | null;

// Einmal beschrieben, zweimal gezeichnet: als Karte und als Pille. Doppelt
// ausgeschrieben driftet sonst irgendwann der Wortlaut der beiden Fassungen.
const WAYS = [
  {
    key: "known",
    title: { id: "project.candidates.knownTitle", defaultMessage: "Aus Community hinzufügen" },
    desc: { id: "project.candidates.knownDesc", defaultMessage: "Aus deiner Community oder über die Suche." },
  },
  {
    key: "existingMatch",
    title: { id: "project.candidates.existingMatchTitle", defaultMessage: "Ich habe schon ein Match" },
    desc: {
      id: "project.candidates.existingMatchDesc",
      defaultMessage: "Ein früheres Match-Ergebnis als Ausgangspunkt übernehmen.",
    },
  },
  {
    key: "match",
    title: { id: "project.candidates.matchTitle", defaultMessage: "Ich will matchen" },
    // Sagt, woher die Anforderungen kommen, und das nennt jede Art anders (`texts.wayDesc`).
    desc: null,
  },
] as const;

const ALL_WAYS: readonly NonNullable<CandidatePath>[] = WAYS.map((way) => way.key);

export function ProjectCandidatesScreen({
  ways = ALL_WAYS,
  candidates,
  onCandidatesChange,
  linkedMatches,
  matchWizard,
  texts,
  path,
  onPathChange,
  onUnlinkMatch,
  readOnly = false,
  showLatestResult = false,
}: {
  /** Person-Ids der Gefragten. */
  candidates: readonly string[];
  onCandidatesChange: (next: readonly string[]) => void;
  /** Matches, die schon zu diesem Projekt gehören. */
  linkedMatches: readonly LinkedMatch[];
  /** Der Zwischenschritt "was geht mit ins Match", von der Seite gerendert. */
  matchWizard: ReactNode;
  /** Was die Fläche je Art anders nennt (`matchFacet` in dashboard-kinds.tsx). */
  texts: KindMatchFacetTexts;
  /** Der offene Weg; null heisst, es steht nur die Auswahl da. */
  path: CandidatePath;
  onPathChange: (next: CandidatePath) => void;
  /** Löst ein verknüpftes Match vom Projekt; das Match selbst bleibt. Fehlt, wenn man nicht ändern darf. */
  onUnlinkMatch?: (matchId: string) => Promise<void>;
  /** Nur lesen (`projectRights`): die Matches dieses Projekts, keine Wege zu neuen Menschen. */
  readOnly?: boolean;
  showLatestResult?: boolean;
  /**
   * Welche Wege die Art anbietet. Eine Ausschreibung hat keine Gefragten, also nur den Weg ins Match;
   * die beiden anderen füllen `data.candidates`.
   */
  ways?: readonly NonNullable<CandidatePath>[];
}) {
  const intl = useIntl();
  // "all" oder die Id einer Community. Die Auswahl filtert die Suche, sie lädt
  // keine zweite Liste: sonst hätte man wieder zwei Wege, Menschen zu finden.
  const [activeGroup, setActiveGroup] = useState<GroupSelection>("all");
  const [onlyWithResult, setOnlyWithResult] = useState(true);
  const [openMatchId, setOpenMatchId] = useState<string | null>(null);
  // Lösen fragt nach wie Löschen (@adr SPARK-0011-destructive-actions-require-confirmation@1); ein Fehler bleibt im Dialog stehen.
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedMatch | null>(null);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  function closeUnlink() {
    setUnlinkTarget(null);
    setUnlinkError(null);
  }

  function toggle(id: string) {
    onCandidatesChange(candidates.includes(id) ? candidates.filter((c) => c !== id) : [...candidates, id]);
  }

  // Die Seite hält die Wahl auch, wenn sie nur lesbar wird (Unternehmenskontext verlassen, eigene Zeile
  // entfernt). Jeder Weg schreibt das Projekt, also öffnet dann keiner.
  const openPath = readOnly ? null : path;

  // Eigene Matches plus, für den Filter, ihre behaltenen Ergebnisse. Zwei
  // Abfragen statt einer: ein Match kennt seine Ergebnisse nicht, und die
  // Ergebnisliste kennt ihren Match nur als Id.
  // Die eigenen Communities für die Auswahl links. Dieselbe Abfrage wie auf der
  // Community-Seite, damit beide Flächen dieselben Namen und Zahlen zeigen.
  const groupsQuery = useQuery(MyPersonGroupsDocument, { skip: openPath !== "known" });
  const contactGroups =
    groupsQuery.data?.personGroups?.__typename === "PersonGroupResult"
      ? (groupsQuery.data.personGroups.personGroups ?? []).flatMap((g) =>
          g ? [{ id: g.id, name: g.name ?? "", itemCount: g.itemCount ?? 0 }] : [],
        )
      : [];

  const matches = useQuery(MatchListDocument, {
    variables: { input: { first: 25, order: { updatedAt: SortOrder.Desc } } },
    skip: openPath !== "existingMatch",
  });
  const matchNodes = useMemo(() => matches.data?.matches?.nodes ?? [], [matches.data]);
  const results = useQuery(MatchResultListDocument, {
    variables: {
      input: {
        first: 100,
        where: { matchId: { in: matchNodes.map((m) => m.id) } },
      },
    },
    skip: openPath !== "existingMatch" || matchNodes.length === 0,
  });
  const resultCountByMatch = useMemo(() => {
    const counts = new Map<string, number>();
    for (const node of results.data?.matchResults?.nodes ?? []) {
      if (node.matchId) counts.set(node.matchId, (counts.get(node.matchId) ?? 0) + 1);
    }
    return counts;
  }, [results.data]);

  // Unternehmens-Matches gehören nicht in diese Liste: sie ranken Unternehmen,
  // und was hier übernommen wird, sind Menschen. Ein Treffer, aus dem man
  // niemanden ins Projekt holen kann, ist an dieser Stelle nur Lärm.
  const personMatches = useMemo(
    () => matchNodes.filter((m) => m.matchMode !== MatchMode.RankCompanies),
    [matchNodes],
  );
  const visibleMatches = onlyWithResult
    ? personMatches.filter((m) => (resultCountByMatch.get(m.id) ?? 0) > 0)
    : personMatches;
  const openMatch = visibleMatches.find((m) => m.id === openMatchId) ?? null;
  const offered = WAYS.filter((way) => ways.includes(way.key));

  return (
    <div>
      {/* DREI WEGE, ZWEI GESTALTEN. Solange nichts da ist, sind sie grosse Karten:
          dann ist die Frage "wie komme ich an Menschen" die einzige auf dieser
          Fläche, und sie darf den Platz nehmen. Sobald Matches existieren, sind
          dieselben Wege nur noch Optionen NEBEN dem Bestand und stehen als Pillen
          in dessen Kopfzeile. Drei grosse Karten über einer gefüllten Liste würden
          behaupten, hier fange etwas an, obwohl es längst läuft. */}
      {linkedMatches.length > 0 ? (
        <div className="mt-4">
          <div className="smycles-field-label mb-2">
            {intl.formatMessage(texts.linkedTitle)}
          </div>
          <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
            {linkedMatches.map((match) =>
              onUnlinkMatch || showLatestResult ? (
                <div key={match.id} className="flex flex-col gap-1.5">
                  <MatchCard match={match} id={`project-linked-match-${match.id}`} />
                  {showLatestResult ? <LatestMatchResultLink matchId={match.id} /> : null}
                  {onUnlinkMatch ? <Button
                    id={`project-unlink-match-${match.id}`}
                    variant="ghost"
                    size="sm"
                    className="self-start"
                    onClick={() => setUnlinkTarget(match)}
                  >
                    <FormattedMessage id="project.candidates.unlink" defaultMessage="Verbindung lösen" />
                  </Button> : null}
                </div>
              ) : (
                <MatchCard key={match.id} match={match} id={`project-linked-match-${match.id}`} />
              ),
            )}
          </div>
          {/* Die Wege stehen UNTER dem Bestand, nicht darüber: was sie öffnen,
              erscheint darunter, und ein Knopf, dessen Wirkung über ihm auftaucht,
              liest sich falsch herum. */}
          {readOnly ? null : (
              <div className="mt-3 flex flex-wrap gap-2">
                {offered.map((way) => (
                  <button
                    key={way.key}
                    id={`project-candidates-way-${way.key}`}
                    type="button"
                    className={`smycles-chip-button ${path === way.key ? "smycles-selected ring-2 ring-accent-decoration" : ""}`}
                    aria-pressed={path === way.key}
                    onClick={() => onPathChange(path === way.key ? null : (way.key as CandidatePath))}
                  >
                    {intl.formatMessage(way.title)}
                  </button>
                ))}
              </div>
          )}
          <ConfirmDialog
            id="project-unlink-dialog"
            open={unlinkTarget !== null}
            title={intl.formatMessage(texts.unlinkTitle)}
            message={
              <>
                {intl.formatMessage(texts.unlinkBody, { name: unlinkTarget?.name ?? "" })}
                {unlinkError ? (
                  <p id="project-unlink-error" role="alert" className="mt-3">
                    {unlinkError}
                  </p>
                ) : null}
              </>
            }
            confirmLabel={<FormattedMessage id="project.candidates.unlink" defaultMessage="Verbindung lösen" />}
            onConfirm={async () => {
              if (!unlinkTarget || !onUnlinkMatch || unlinking) return;
              setUnlinking(true);
              setUnlinkError(null);
              try {
                await onUnlinkMatch(unlinkTarget.id);
                closeUnlink();
              } catch (e) {
                setUnlinkError(localizedMessageOf(e) ?? userErrorMessage(intl, e));
              } finally {
                setUnlinking(false);
              }
            }}
            onCancel={closeUnlink}
          />
        </div>
      ) : readOnly ? (
        <p className="smycles-field-meta mt-4 text-ink-muted">
          {intl.formatMessage(texts.noLinked)}
        </p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-3 @sm:grid-cols-3">
          {offered.map((way) => (
            <button
              key={way.key}
              id={`project-candidates-way-${way.key}`}
              type="button"
              aria-pressed={path === way.key}
              onClick={() => onPathChange(path === way.key ? null : (way.key as CandidatePath))}
              className={`box-button flex h-full flex-col p-4 text-left transition hover:-translate-y-0.5 ${
                path === way.key ? "smycles-selected ring-2 ring-accent-decoration" : ""
              }`}
            >
              <div className="smycles-field-value font-medium">{intl.formatMessage(way.title)}</div>
              <div className="smycles-field-meta mt-1.5 leading-snug text-ink">
                {intl.formatMessage(way.desc ?? texts.wayDesc)}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Auch der Weg ins Match klappt HIER auf, statt auf eine eigene Seite zu
          springen. Drei gleichrangige Angebote, von denen eines den Reiter
          verlaesst, waeren drei Angebote mit einer Ausnahme - und die merkt sich
          niemand. */}
      {openPath === "match" ? <div className="reveal-rise motion-reduce:animate-none motion-reduce:opacity-100 mt-4">{matchWizard}</div> : null}

      {/* Die vollständige Personensuche, eingebettet: dasselbe Suchfeld, dieselben
          Teamplayer-Filter und dieselbe Trefferliste wie unter "Discover".
          Vorher stand hier ein eigenes Feld über zwölf geladenen Kontakten - es
          sah aus wie die Suche, konnte aber weder filtern noch weiterblättern,
          und wer mehr als zwölf Kontakte hat, sah den Rest nie. Eine zweite,
          schwächere Suche neben der echten ist kein Feature, sondern eine
          Stelle, an der beide auseinanderlaufen. */}
      {openPath === "known" ? (
        <div className="reveal-rise motion-reduce:animate-none motion-reduce:opacity-100 mt-4 flex flex-col gap-5 @lg:flex-row">
          {/* Die Communities links, wie auf der Community-Seite: derselbe
              Baustein, dieselbe Reihenfolge, dieselben Zähler. Sie sind hier
              eine SICHT auf die Suche, kein eigener Bestand - deshalb ohne
              Anlegen, Umbenennen und Löschen, das gehört auf die Seite, der die
              Gruppen gehören. */}
          <div className="@lg:w-56 @lg:shrink-0">
            <SaveCollectionSidebar
              embedded
              groups={contactGroups}
              activeGroup={activeGroup}
              allLabel={intl.formatMessage({ id: "project.candidates.allContacts", defaultMessage: "Alle" })}
              onGroupChange={(next) => {
                setActiveGroup(next);
                // Eine andere Community hat eine andere Trefferzahl, die Seite
                // wird also kürzer oder länger. Ohne Anker landet der Blick
                // irgendwo, meist über oder unter der Liste, die man gerade
                // gewählt hat.
                document
                  .getElementById("project-candidate-results")
                  ?.scrollIntoView({ block: "start", behavior: "smooth" });
              }}
            />
          </div>
          {/* Eigener @container: die Rasterklassen der Trefferliste (@sm/@lg/@xl)
              messen sonst die AppShell-Fläche und schalten auf vier Spalten,
              obwohl diese Spalte neben der Gruppenliste viel schmaler ist. Genau
              das liess die Karten auf Avatar und Knopf zusammenschrumpfen, der
              Name hatte keinen Platz mehr. Ein Container misst, was er wirklich
              ist. */}
          <div id="project-candidate-results" className="@container min-w-0 flex-1 scroll-mt-24">
          <ExtendedSearchPage
            embedded
            scopeFilter={activeGroup === "all" ? null : { personGroupId: { eq: activeGroup } }}
            personActions={(person: { id: string }) => {
              const isPicked = candidates.includes(person.id);
              return (
                <Button variant={isPicked ? "primary" : "secondary"} size="sm" onClick={() => toggle(person.id)}>
                  {isPicked ? (
                    <FormattedMessage id="project.candidates.picked" defaultMessage="Hinzugefügt" />
                  ) : (
                    <FormattedMessage id="project.candidates.pick" defaultMessage="Hinzufügen" />
                  )}
                </Button>
              );
            }}
          />
          </div>
          <p className="smycles-field-meta mt-2 text-ink-muted">
            <FormattedMessage
              id="project.candidates.pickedNote"
              defaultMessage="Gefragte bekommen noch keinen Zugriff aufs Projekt, lassen sich aber schon für Aufgaben einplanen."
            />
          </p>
        </div>
      ) : null}

      {openPath === "existingMatch" ? (
        <div className="reveal-rise motion-reduce:animate-none motion-reduce:opacity-100 mt-4 max-w-2xl">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <div className="smycles-field-label">
              <FormattedMessage
                id="project.candidates.existingMatchLabel"
                defaultMessage="Deine bisherigen Matches"
              />
            </div>
            {/* Der Schalter trägt seine Beschriftung daneben statt als An/Aus-Wortpaar:
                "Nur mit Ergebnis" ist die Bedingung, nicht der Zustand. */}
            <label className="flex items-center gap-2">
              <span className="smycles-field-meta text-ink">
                <FormattedMessage
                  id="project.candidates.onlyWithResult"
                  defaultMessage="Nur mit Ergebnis"
                />
              </span>
              <Switch
                id="project-candidates-onlywithresult"
                checked={onlyWithResult}
                onChange={setOnlyWithResult}
                ariaLabel={intl.formatMessage({
                  id: "project.candidates.onlyWithResult",
                  defaultMessage: "Nur mit Ergebnis",
                })}
              />
            </label>
          </div>
          {/* Das Detail steht als volle Rasterzeile DIREKT HINTER seiner Kachel.
              Im Grid rückt ein Element, das alle Spalten belegt, automatisch in
              die nächste Zeile - also genau unter die Reihe, in der die gewählte
              Kachel steht, und das bei einer, zwei oder drei Spalten gleich
              richtig. Unter der ganzen Liste stehend musste man sich merken,
              welches Match man angeklickt hatte. */}
          <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @lg:grid-cols-3">
            {visibleMatches.map((match) => (
              <Fragment key={match.id}>
                <MatchCard
                  match={match}
                  id={`project-match-option-${match.id}`}
                  selected={openMatchId === match.id}
                  onSelect={() => setOpenMatchId(openMatchId === match.id ? null : match.id)}
                  extra={
                    <>
                      {" · "}
                      <FormattedMessage
                        id="project.candidates.resultCount"
                        defaultMessage="{count, plural, =0 {kein Ergebnis} one {1 Ergebnis} other {# Ergebnisse}}"
                        values={{ count: resultCountByMatch.get(match.id) ?? 0 }}
                      />
                    </>
                  }
                />
                {openMatch?.id === match.id ? (
                  <div className="box-flat col-span-full bg-page p-4">
                    <ProjectMatchResults
                      matchId={openMatch.id}
                      candidates={candidates}
                      onToggleCandidate={toggle}
                      onClose={() => setOpenMatchId(null)}
                      matchHref={`/match/${openMatch.id}`}
                    />
                  </div>
                ) : null}
              </Fragment>
            ))}
          </div>
          {visibleMatches.length === 0 ? (
            <p className="smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="project.candidates.noMatches"
                defaultMessage="Kein Match mit Ergebnis. Schalte den Filter aus, um alle zu sehen."
              />
            </p>
          ) : null}
        </div>
      ) : null}

    </div>
  );
}
